import { useParams } from "react-router-dom";
import { useState } from "react";
import TerminalPageView from "./TerminalPageView";
import useCustomEffect from "../../../hooks/useCustomEffect";
import { useRouteContext } from "../../../context/RouteContext";
import { useAuthContext } from "../../../context/AuthContext";
import useEvaApi, { EvaApiCall } from "../../../hooks/useEvaApi";

function TerminalPage() {
  const callEvaApi = useEvaApi();
  const routeContext = useRouteContext();
  const authContext = useAuthContext();
  const { terminal } = useParams();
  const [submitError, setSubmitError] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [visitorTypes, setVisitorTypes] = useState([]);
  const [screensavers, setScreensavers] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [terminalData, setTerminalData] = useState({
    name: "",
    show_sign_in_button: true,
    show_sign_out_button: true,
    show_qr_code_button: true,
    show_employee_registration_button: false,
    status_after_sign_in: "signed_in",
    location_id: authContext.auth.location,
    layout_id: null,
    printer_id: null,
    language_id: null,
    buttons: [],
    visitor_types: [],
    screensavers: [],
  });

  /**
   * initialize the page
   */
  useCustomEffect(() => {
    if (terminal) {
      fetchTerminalData();
    }

    fetchButtons();
    fetchVisitorTypes();
    fetchScreensavers();
    fetchLayouts();
    fetchPrinters();
  });

  /**
   * This method will fetch the terminal data from the API
   */
  async function fetchTerminalData() {
    await callEvaApi(
      new EvaApiCall(`terminals/${terminal}`)
        .setLoadingGroup("terminal_page_fetch")
        .setParams({
          with_terminal_visitor_types_data: true,
        })
        .setOnSuccess((response) => {
          setTerminalData({
            name: response.data.data.name,
            show_sign_in_button: response.data.data.show_sign_in_button,
            show_sign_out_button: response.data.data.show_sign_out_button,
            show_qr_code_button: response.data.data.show_qr_code_button,
            show_employee_registration_button:
              response.data.data.show_employee_registration_button,
            status_after_sign_in: response.data.data.status_after_sign_in,
            location_id: response.data.data.location,
            layout_id: response.data.data.layout,
            printer_id: response.data.data.printer,
            language_id: response.data.data.language,
            buttons: response.data.data.buttons,
            visitor_types: response.data.data.visitor_types.map(
              (visitorType) => ({
                visitor_type_id: visitorType.visitor_type,
                show_button: visitorType.show_button,
              }),
            ),
            screensavers: response.data.data.screensavers,
          });
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the button data from API
   */
  async function fetchButtons() {
    await callEvaApi(
      new EvaApiCall("buttons")
        .setLoadingGroup("terminal_page_fetch")
        .setOnSuccess((response) => {
          setButtons(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the visitor type data from API
   */
  async function fetchVisitorTypes() {
    await callEvaApi(
      new EvaApiCall("visitor_types")
        .setLoadingGroup("terminal_page_fetch")
        .setOnSuccess((response) => {
          setVisitorTypes(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the screensavers from API
   */
  async function fetchScreensavers() {
    await callEvaApi(
      new EvaApiCall("files")
        .setParams({
          where: [{ column: "type", values: ["image"] }],
        })
        .setLoadingGroup("terminal_page_fetch")
        .setOnSuccess((response) => {
          setScreensavers(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the layouts from API
   */
  async function fetchLayouts() {
    await callEvaApi(
      new EvaApiCall("layouts")
        .setLoadingGroup("terminal_page_fetch")
        .setOnSuccess((response) => {
          setLayouts(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will fetch the visitor type data from API
   */
  async function fetchPrinters() {
    await callEvaApi(
      new EvaApiCall("printers")
        .setLoadingGroup("terminal_page_fetch")
        .setOnSuccess((response) => {
          setPrinters(response.data.data.records);
        })
        .setRedirectOnError(true),
    );
  }

  /**
   * This method will update the buttons on input change
   * @param {array} values
   */
  function onButtonsChange(values) {
    setTerminalData({
      ...terminalData,
      buttons: values,
    });
  }

  /**
   * This method will update the screensavers on input change
   * @param {array} values
   */
  function onScreensaversChange(values) {
    setTerminalData({
      ...terminalData,
      screensavers: values,
    });
  }

  /**
   * This method will update the name on input change
   */
  function onNameChange(e) {
    setTerminalData((current) => ({ ...current, name: e.target.value }));
  }

  /**
   * This method will update the name on input change
   */
  function onLocationIdChange(value) {
    setTerminalData((current) => ({ ...current, location_id: value }));
  }

  /**
   * This method will update the layout id on input change
   * @param {any} value
   */
  function onLayoutIdChange(value) {
    setTerminalData((current) => ({ ...current, layout_id: value }));
  }

  /**
   * This method will update the printer id on input change
   * @param {any} value
   */
  function onPrinterIdChange(value) {
    setTerminalData((current) => ({ ...current, printer_id: value }));
  }
  /**
   * This method will update the language id on input change
   * @param {any} value
   */
  function onLanguageIdChange(value) {
    setTerminalData((current) => ({ ...current, language_id: value }));
  }
  /**
   * This method will update the show sign in button on input change
   * @param {boolean} value
   */
  function onShowSignInButtonChange(value) {
    setTerminalData((current) => ({ ...current, show_sign_in_button: value }));
  }

  /**
   * This method will update the show sign out button on input change
   * @param {boolean} value
   */
  function onShowSignOutButtonChange(value) {
    setTerminalData((current) => ({ ...current, show_sign_out_button: value }));
  }

  /**
   * This method will update the show qr code button on input change
   * @param {boolean} value
   */
  function onShowQrCodeButtonChange(value) {
    setTerminalData((current) => ({ ...current, show_qr_code_button: value }));
  }

  /**
   * This method will update the show employee registration button on input change
   * @param {boolean} value
   */
  function onShowEmployeeRegistrationButtonChange(value) {
    setTerminalData((current) => ({
      ...current,
      show_employee_registration_button: value,
    }));
  }

  /**
   * This method will update the status after sign in on input change
   * @param {string} value
   */
  function onStatusAfterSignInChange(value) {
    setTerminalData((current) => ({ ...current, status_after_sign_in: value }));
  }

  /**
   * This method will handle the onCancel functionality
   */
  function onCancel() {
    routeContext.back();
  }

  /**
   * This method will submit the form to the EVA API and handles its response
   */
  async function onSubmit() {
    await callEvaApi(
      new EvaApiCall(terminal ? `terminals/${terminal}` : "terminals")
        .setLoadingGroup("terminal_page_save")
        .setMethod(terminal ? "PUT" : "POST")
        .setData(terminalData)
        .setErrorState(setSubmitError)
        .setRedirectOnSuccess(true),
    );
  }

  return (
    <TerminalPageView
      terminal={terminal}
      terminalData={terminalData}
      setTerminalData={setTerminalData}
      buttons={buttons}
      visitorTypes={visitorTypes}
      screensavers={screensavers}
      layouts={layouts}
      printers={printers}
      onButtonsChange={onButtonsChange}
      onNameChange={onNameChange}
      onLocationIdChange={onLocationIdChange}
      onLayoutIdChange={onLayoutIdChange}
      onPrinterIdChange={onPrinterIdChange}
      onLanguageIdChange={onLanguageIdChange}
      onScreensaversChange={onScreensaversChange}
      onShowSignInButtonChange={onShowSignInButtonChange}
      onShowSignOutButtonChange={onShowSignOutButtonChange}
      onShowQrCodeButtonChange={onShowQrCodeButtonChange}
      onShowEmployeeRegistrationButtonChange={
        onShowEmployeeRegistrationButtonChange
      }
      onStatusAfterSignInChange={onStatusAfterSignInChange}
      onCancel={onCancel}
      onSubmit={onSubmit}
      submitError={submitError}
    />
  );
}

export default TerminalPage;
